// The boot-time auth sequence — the subtlest lines of the frontend.
//
// The ORDER is the design (account-linking design, "Resolution" + the boot
// ruling): paint from the session cache first so the page renders instantly,
// then — only when this load is a sign-in return — complete the exchange. The
// identity server classifies the login EXACTLY there, once per login, because
// the callback is the only moment a login can be new; ordinary boots just
// hydrate. A link-mode return diverts to the attach and never becomes the
// session.
import { brokeredLogin } from './brokeredLogin.js';
import { identityServerClient } from './identityServerClient.js';
import { setPendingLogin, takeParkedLogin } from './pendingLogin.js';
import { setSession } from './sessionTokenStorage.js';

// What the identity server calls a login no account owns yet → the outcome a
// surface routes on.
const PENDING_OUTCOMES = Object.freeze({
    RESTORABLE: 'restorable',
    MATCH_FOUND: 'match-found',
    ACCOUNT_NOT_LINKED: 'unlinked'
});

function attachFailure(error) {
    return error?.body?.error || `ATTACH_FAILED_${error?.statusCode ?? 'NETWORK'}`;
}

/**
 * Runs the boot auth sequence. Exactly one of these outcomes:
 *
 *   { outcome: 'no-callback' }               — a plain boot; hydrate ran against
 *                                              whatever session was persisted.
 *   { outcome: 'authenticated', targetPath,  — a login resolved to a Human. If a
 *     linked?, linkError? }                    parked "connect this login" login
 *                                              was waiting, it was attached
 *                                              (`linked: true`) or failed
 *                                              (`linkError`).
 *   { outcome: 'unlinked' }                  — the provider vouched for the login
 *                                              but no Human owns it. Its token is
 *                                              KEPT (the choice screen's calls
 *                                              present it); the person chooses
 *                                              link-existing / create-new.
 *   { outcome: 'restorable' }                — this login belonged to a closed
 *                                              account; offer restore-or-fresh.
 *   { outcome: 'match-found', providers }    — no linked account, but the verified
 *                                              email already anchors one; offer a
 *                                              one-tap connect. The token is KEPT.
 *   { outcome: 'linked', targetPath }        — a link-mode return attached its
 *                                              login to the signed-in Human.
 *   { outcome: 'link-failed', error }        — the link-mode return could not
 *                                              attach (cancelled, conflict, …).
 *   { outcome: 'failed', error }             — the sign-in came back with an error,
 *                                              or the exchange failed.
 */
export async function completeBootAuth({ session }) {
    await session.paintFromCache();
    const loginResult = await brokeredLogin.completeLoginIfCallback();

    if (!loginResult) {
        await session.hydrate();
        return { outcome: 'no-callback' };
    }

    if (loginResult.mode === 'link') {
        // The owner's session is the stored one; what came back names the login
        // to attach, held only long enough to hand it over.
        await session.hydrate();
        if (loginResult.error) {
            return { outcome: 'link-failed', error: loginResult.error };
        }
        if (!session.isAuthenticated) {
            return { outcome: 'link-failed', error: 'NOT_SIGNED_IN' };
        }
        try {
            await identityServerClient.attachLogin(loginResult.pendingLogin.id);
            return { outcome: 'linked', targetPath: loginResult.targetPath };
        } catch (e) {
            return { outcome: 'link-failed', error: attachFailure(e) };
        }
    }

    if (loginResult.error) {
        await session.hydrate();
        return { outcome: 'failed', error: loginResult.error };
    }

    if (loginResult.pendingLogin) {
        // Token kept: the choice screen's create / restore / connect present it.
        // The guard routes on the outcome, not the token.
        setPendingLogin({
            accessToken: loginResult.pendingLogin.accessToken,
            id: loginResult.pendingLogin.id,
            identity: loginResult.pendingLogin.identity
        });
        const outcome = PENDING_OUTCOMES[loginResult.pendingLogin.outcome] || PENDING_OUTCOMES.ACCOUNT_NOT_LINKED;
        if (outcome === PENDING_OUTCOMES.MATCH_FOUND) {
            // `providers` names the existing login methods for the chip.
            return { outcome, providers: loginResult.pendingLogin.providers };
        }
        return { outcome };
    }

    await setSession(loginResult.tokens);
    await session.hydrate();
    // A parked "connect this login to my existing account" login completes its
    // hand-off now that the owner's session stands.
    const parked = takeParkedLogin();
    if (parked) {
        try {
            await identityServerClient.attachLogin(parked);
            return { outcome: 'authenticated', targetPath: loginResult.targetPath, linked: true };
        } catch (e) {
            return { outcome: 'authenticated', targetPath: loginResult.targetPath, linkError: attachFailure(e) };
        }
    }
    return { outcome: 'authenticated', targetPath: loginResult.targetPath };
}
